import type { EraDef } from '../types';

/**
 * ERA 4 — ICE AGE
 * Identity: "the ground is slippery and the hunters come in packs." Wide, glassy
 * tundra; enemies either skid past you with no steering (`slide`) or circle at
 * range until enough of them gather to commit as a pack (`pack`). Patterns lean
 * on slow, wide, freezing volleys (decelerating orbs that hang at a crawl, and
 * drifting waves of snow) instead of era 3's heavy homing. Hazard: freezing
 * patches left by trails and lingering hits. Boss: Mammoth King, who leaves a
 * freezing slick everywhere it walks and, in its final phase, skids across the
 * arena itself.
 * Palette: pale ice blues/whites, warm amber enemy bullets for contrast.
 */
const ENEMY_BULLET = 0xffa64d;
const HAZARD = 0x8fd6ff;

export const ERA_ICEAGE: EraDef = {
  index: 3,
  id: 'iceage',
  name: 'Ice Age',
  subtitle: '20,000 years ago',
  implemented: true,
  palette: {
    sky: 0x0d1a2b,
    fog: 0x18324d,
    fogDensity: 0.02,
    ground: 0xdfe9f2,
    groundAlt: 0xaac2d6,
    ambient: 0x6f95b8,
    sun: 0xcfefff,
    sunIntensity: 1.3,
    accent: 0x4de8ff,
    enemyBullet: ENEMY_BULLET,
    hazard: HAZARD,
  },
  environment: {
    kind: 'tundra',
    arenaRadius: 70,
    groundStyle: 'snow',
    props: [
      { visual: { geo: 'cone', color: 0xeaf4fb, emissive: 0xaad6f2, emissiveIntensity: 0.3, scaleY: 1.8, anim: 'none' }, count: 24, scale: [0.6, 1.9], y: 0 },
      { visual: { geo: 'box', color: 0xffffff, emissive: 0xbcd8ee, emissiveIntensity: 0.15, anim: 'none' }, count: 22, scale: [0.4, 1.3], y: 0 },
      { visual: { geo: 'capsule', color: 0xc9d8e4, scaleY: 5, anim: 'none' }, count: 12, scale: [0.4, 0.9], y: 1.2 },
    ],
  },
  enemies: [
    {
      id: 'i4_ice_rat',
      name: 'Ice Rat',
      archetype: 'swarmer',
      hp: 15,
      speed: 4.2,
      damage: 8,
      radius: 0.4,
      xp: 5,
      visual: { geo: 'sphere', color: 0xbfe3ff, emissive: 0x2a5a80, emissiveIntensity: 0.4, scale: 0.8, anim: 'wobble' },
      // Filler swarmer: just runs at you. Counterplay: cheap kills, don't let them pile up contact hits.
      behavior: { type: 'chase' },
    },
    {
      id: 'i4_dire_wolf',
      name: 'Dire Wolf',
      archetype: 'swarmer',
      hp: 72,
      speed: 4,
      damage: 18,
      radius: 0.55,
      xp: 13,
      visual: { geo: 'cone', color: 0x7a8a9a, emissive: 0x2a3a48, emissiveIntensity: 0.4, scale: 1, scaleY: 1.2, anim: 'bob' },
      // Circles at range (slower than you at 4 u/s) until 3 pack-mates are within 5 units of it, then the
      // whole pack commits and chases at 1.6x — suddenly faster than you. Counterplay: thin the pack before
      // quorum, or break line of sight and make them re-gather.
      behavior: { type: 'pack', distance: 7, commitCount: 3, radius: 5, commitSpeedMult: 1.6 },
    },
    {
      id: 'i4_glacier_ram',
      name: 'Glacier Ram',
      archetype: 'charger',
      hp: 95,
      speed: 2.2,
      damage: 20,
      radius: 0.75,
      xp: 16,
      visual: { geo: 'box', color: 0x8a9aab, emissive: 0x2a3a48, emissiveIntensity: 0.35, scale: 1.2, scaleY: 1.1, anim: 'none' },
      // Heavy horned charger: stands stock-still facing you during the wind-up, then launches with momentum
      // and no steering, skidding past on the ice. Counterplay: sidestep at the last moment and it overshoots.
      behavior: { type: 'slide', range: 9, windup: 0.75, impulse: 15, friction: 2.2, cooldown: 2.6 },
    },
    {
      id: 'i4_frost_sabertooth',
      name: 'Frost Sabertooth',
      archetype: 'shooter',
      hp: 47,
      speed: 2.8,
      damage: 14,
      radius: 0.55,
      xp: 13,
      visual: { geo: 'cone', color: 0x7fb8e0, emissive: 0x1c3a5c, emissiveIntensity: 0.5, scale: 1, anim: 'wobble' },
      // Ranged kiter: alternates a decelerating freezing orb (hangs, leaves a patch) with a slow, wide wall
      // that carves the arena into lanes. Counterplay: pick a lane early, the wall telegraphs its gaps.
      behavior: { type: 'keepDistance', distance: 9, strafe: 0.3 },
      patterns: ['i4_frost_orb', 'i4_frost_wall'],
      patternCooldown: 2,
      fireRange: 14,
    },
    {
      id: 'i4_mammoth_calf',
      name: 'Mammoth Calf',
      archetype: 'tank',
      hp: 240,
      speed: 1.3,
      damage: 23,
      radius: 1.25,
      xp: 30,
      shardChance: 0.18,
      visual: { geo: 'blob', color: 0x9c7a54, emissive: 0x3a2a18, emissiveIntensity: 0.4, scale: 2.2, anim: 'none' },
      // Slow tank that drags a freezing-patch trail while it walks and, on a long cooldown, lobs a ring of
      // fat, decelerating orbs. Counterplay: don't linger in its wake, and give the orb ring room to hang.
      behavior: { type: 'chase' },
      patterns: ['i4_mammoth_orb'],
      patternCooldown: 4,
      trail: { radius: 0.6, duration: 1.8, dps: 5, interval: 0.5, color: HAZARD },
    },
    {
      id: 'i4_snow_owl_nest',
      name: 'Snow Owl Nest',
      archetype: 'splitter',
      hp: 32,
      speed: 0,
      damage: 6,
      radius: 0.7,
      xp: 9,
      shardChance: 0.3,
      visual: { geo: 'sphere', color: 0xf0f6fb, emissive: 0x9ab8d0, emissiveIntensity: 0.4, scale: 1.1, anim: 'pulse' },
      // Stationary lobber: arcs slow, decelerating snowballs that leave a freezing patch on landing.
      // Counterplay: the orbs crawl to a stop — a steady walk carries you clear well before they land near you.
      behavior: { type: 'stationary' },
      patterns: ['i4_snow_lob'],
      patternCooldown: 2.5,
      fireRange: 12,
      onDeath: { spawn: { enemy: 'i4_ice_rat', count: 4, spread: 1.4 } },
    },
    {
      id: 'i4_alpha_direwolf',
      name: 'Alpha Dire Wolf',
      archetype: 'elite',
      hp: 150,
      speed: 4.3,
      damage: 26,
      radius: 0.75,
      xp: 34,
      shardChance: 0.4,
      elite: true,
      visual: { geo: 'cone', color: 0xd8e6f2, emissive: 0x9ab8d0, emissiveIntensity: 0.7, scale: 1.3, scaleY: 1.2, anim: 'spin' },
      // Elite pack leader: same pack quorum trick but commits sooner (needs only 2 nearby) and faster, and
      // peppers shards while circling. Counterplay: same as regular wolves, but expect the commit earlier.
      behavior: { type: 'pack', distance: 7, commitCount: 2, radius: 6, commitSpeedMult: 1.7 },
      patterns: ['i4_wolf_alpha_burst'],
      patternCooldown: 1.6,
      fireRange: 16,
    },
  ],
  patterns: [
    // ---- enemy patterns ----
    {
      // Decelerating freezing orb: crawls to a near-stop and hangs, leaving a freezing patch on expiry —
      // era 4's pattern signature (slow, wide, freezing) instead of era 3's heavy homing.
      id: 'i4_frost_orb',
      emitters: [
        {
          shape: 'aimed',
          interval: 0.9,
          bursts: 3,
          count: 3,
          spread: 0.35,
          bullet: { speed: 8, accel: -2.5, minSpeed: 1.3, lifetime: 3.6, radius: 0.32, damage: 13, visual: 'orb', color: ENEMY_BULLET, linger: { radius: 1.0, duration: 3.5, dps: 6, color: HAZARD } },
        },
      ],
    },
    {
      // Wide, slow wall that carves the arena into safe lanes.
      id: 'i4_frost_wall',
      emitters: [{ shape: 'wall', interval: 0.6, bursts: 2, count: 11, spread: 9, gaps: 3, bullet: { speed: 4.5, lifetime: 4, radius: 0.28, damage: 13, visual: 'ring', color: ENEMY_BULLET } }],
    },
    {
      // Big, fat, slow orbs — the mammoth calf's long-cooldown lob.
      id: 'i4_mammoth_orb',
      emitters: [{ shape: 'radial', interval: 1.6, bursts: 1, count: 10, bullet: { speed: 3.5, accel: -1.2, minSpeed: 1, lifetime: 2.6, radius: 0.5, damage: 16, visual: 'orb', color: ENEMY_BULLET } }],
    },
    {
      // Slow, arcing lob that leaves a freezing patch — owl nest's turret attack.
      id: 'i4_snow_lob',
      emitters: [{ shape: 'aimed', interval: 1.5, bursts: 3, count: 1, spread: 0.5, bullet: { speed: 4, accel: -1.5, minSpeed: 1, lifetime: 4, radius: 0.34, damage: 12, visual: 'blob', color: HAZARD, linger: { radius: 1.0, duration: 3, dps: 6, color: HAZARD } } }],
    },
    {
      id: 'i4_wolf_alpha_burst',
      emitters: [{ shape: 'radial', interval: 1.2, bursts: 3, count: 8, bullet: { speed: 6, lifetime: 3, radius: 0.22, damage: 13, visual: 'shard', color: ENEMY_BULLET } }],
    },
    // ---- boss patterns ----
    {
      // Phase-1 onEnter: a single wide, slow opening ring — the "blizzard begins" telegraph.
      id: 'p4_blizzard_open',
      emitters: [{ shape: 'radial', interval: 1, bursts: 1, count: 18, bullet: { speed: 3, lifetime: 1.6, radius: 0.5, damage: 14, visual: 'ring', color: ENEMY_BULLET } }],
    },
    {
      // Large, decelerating orb ring — the boss's core "slow, wide, freezing" attack.
      id: 'i4_boss_orb_ring',
      emitters: [{ shape: 'radial', interval: 1.8, bursts: 1, count: 14, bullet: { speed: 3.2, accel: -1, minSpeed: 1, lifetime: 3.5, radius: 0.5, damage: 17, visual: 'orb', color: ENEMY_BULLET } }],
    },
    {
      // Wide advancing wall, repeated — carves the whole arena into lanes.
      id: 'i4_boss_wall_spread',
      emitters: [{ shape: 'wall', interval: 0.7, bursts: 3, count: 13, spread: 11, gaps: 4, bullet: { speed: 4.5, lifetime: 4.5, radius: 0.3, damage: 16, visual: 'ring', color: ENEMY_BULLET } }],
    },
    {
      // Drifting snow: slow bullets that wave sideways and hang around — no homing, just weave through them.
      id: 'i4_boss_snowdrift',
      duration: 3.6,
      emitters: [{ shape: 'radial', interval: 0.5, bursts: 0, count: 6, bullet: { speed: 3, wave: { amp: 1.3, freq: 0.5 }, lifetime: 5, radius: 0.3, damage: 14, visual: 'blob', color: HAZARD } }],
    },
    {
      // Phase-3 onEnter: a big double ring of slow orbs — the avalanche breaking loose.
      id: 'p4_avalanche_enter',
      emitters: [
        { shape: 'radial', interval: 1, bursts: 1, count: 20, bullet: { speed: 3.6, lifetime: 2, radius: 0.5, damage: 20, visual: 'orb', color: ENEMY_BULLET } },
        { shape: 'radial', interval: 1, start: 0.25, bursts: 1, count: 20, bullet: { speed: 2, lifetime: 2.4, radius: 0.5, damage: 20, visual: 'ring', color: HAZARD } },
      ],
    },
    {
      // Dense flurry of decelerating shards that leave freezing patches — phase 3's real pressure.
      id: 'i4_boss_meteor_flurry',
      emitters: [
        {
          shape: 'random',
          interval: 0.25,
          bursts: 9,
          count: 3,
          spread: 6.283,
          bullet: { speed: 6, accel: -3, minSpeed: 0.6, lifetime: 2.6, radius: 0.34, damage: 16, visual: 'blob', color: HAZARD, linger: { radius: 1.3, duration: 4, dps: 8, color: HAZARD } },
        },
      ],
    },
  ],
  boss: {
    id: 'i4_mammoth_king',
    name: 'Mammoth King',
    title: 'Lord of the Blizzard',
    hp: 2900,
    radius: 2.2,
    speed: 1.6,
    damage: 34,
    xp: 480,
    visual: { geo: 'blob', color: 0x9c7a54, emissive: 0xdfefff, emissiveIntensity: 0.4, scale: 1.5, anim: 'none' },
    phases: [
      {
        // Teaching phase: readable keepDistance kiting + two slow, wide patterns, opened with a wide ring.
        // No special yet.
        hpBelow: 1,
        name: 'Herd',
        behavior: { type: 'keepDistance', distance: 11, strafe: 0.25 },
        patterns: ['i4_boss_orb_ring', 'i4_boss_wall_spread'],
        patternCooldown: 2.2,
        transition: 1.5,
        onEnter: 'p4_blizzard_open',
      },
      {
        // Blizzard phase: starts trailing a freezing slick, denser pattern set, and calls in ice rats.
        hpBelow: 0.65,
        name: 'Blizzard',
        behavior: { type: 'chase' },
        patterns: ['i4_boss_wall_spread', 'i4_boss_snowdrift'],
        patternCooldown: 1.4,
        special: { type: 'trail', trail: { radius: 0.9, duration: 2.2, dps: 6, interval: 0.4, color: HAZARD } },
        summons: { enemy: 'i4_ice_rat', count: 5, interval: 6 },
        transition: 1.8,
        speedMult: 1.15,
      },
      {
        // Avalanche phase: the boss itself skids across the arena (slide, no steering) while a fatter slick
        // closes in the arena, opened by a big double-ring burst, reinforced by dire wolves.
        hpBelow: 0.3,
        name: 'Avalanche',
        behavior: { type: 'slide', range: 12, windup: 1, impulse: 16, friction: 1.8, cooldown: 3.5 },
        patterns: ['i4_boss_meteor_flurry', 'i4_boss_orb_ring'],
        patternCooldown: 1,
        special: { type: 'trail', trail: { radius: 1.4, duration: 3, dps: 8, interval: 0.35, color: HAZARD } },
        summons: { enemy: 'i4_dire_wolf', count: 2, interval: 8 },
        transition: 2,
        speedMult: 1.3,
        onEnter: 'p4_avalanche_enter',
      },
    ],
  },
  stages: [
    {
      name: 'Frozen Tundra',
      duration: 210,
      objective: { type: 'hold', seconds: 60, radius: 8 },
      density: { start: 1.0, end: 2.6 },
      pool: [
        { enemy: 'i4_ice_rat', weight: 10 },
        { enemy: 'i4_frost_sabertooth', weight: 4, minTime: 20 },
        { enemy: 'i4_dire_wolf', weight: 4, minTime: 30 },
        { enemy: 'i4_glacier_ram', weight: 3, minTime: 50 },
        { enemy: 'i4_snow_owl_nest', weight: 2, minTime: 70 },
      ],
      waves: [
        { t: 4, enemy: 'i4_ice_rat', count: 4, formation: 'ring' },
        { t: 25, enemy: 'i4_frost_sabertooth', count: 3, formation: 'line' },
        { t: 50, enemy: 'i4_ice_rat', count: 16, formation: 'cluster' },
        { t: 75, enemy: 'i4_dire_wolf', count: 4, formation: 'edge' },
        { t: 100, enemy: 'i4_glacier_ram', count: 3, formation: 'random' },
        { t: 130, enemy: 'i4_snow_owl_nest', count: 2, formation: 'edge' },
        { t: 160, enemy: 'i4_ice_rat', count: 20, formation: 'ring' },
        { t: 185, enemy: 'i4_alpha_direwolf', count: 1, formation: 'edge', elite: true },
      ],
    },
    {
      name: 'Glacier Fields',
      duration: 240,
      density: { start: 1.4, end: 3.2 },
      pool: [
        { enemy: 'i4_ice_rat', weight: 8 },
        { enemy: 'i4_dire_wolf', weight: 5 },
        { enemy: 'i4_frost_sabertooth', weight: 5 },
        { enemy: 'i4_glacier_ram', weight: 4 },
        { enemy: 'i4_snow_owl_nest', weight: 2 },
        { enemy: 'i4_mammoth_calf', weight: 2, minTime: 60 },
      ],
      waves: [
        { t: 5, enemy: 'i4_dire_wolf', count: 6, formation: 'ring' },
        { t: 40, enemy: 'i4_mammoth_calf', count: 2, formation: 'line' },
        { t: 75, enemy: 'i4_frost_sabertooth', count: 6, formation: 'ring' },
        { t: 105, enemy: 'i4_alpha_direwolf', count: 1, formation: 'edge', elite: true },
        { t: 135, enemy: 'i4_ice_rat', count: 30, formation: 'ring' },
        { t: 165, enemy: 'i4_glacier_ram', count: 6, formation: 'random' },
        { t: 195, enemy: 'i4_mammoth_calf', count: 3, formation: 'cluster' },
        { t: 220, enemy: 'i4_alpha_direwolf', count: 2, formation: 'line', elite: true },
      ],
    },
    {
      name: "The King's Blizzard",
      duration: 600,
      density: { start: 1.2, end: 1.2 },
      pool: [
        { enemy: 'i4_ice_rat', weight: 6 },
        { enemy: 'i4_dire_wolf', weight: 3 },
      ],
      waves: [{ t: 2, enemy: 'i4_ice_rat', count: 10, formation: 'ring' }],
      boss: { id: 'i4_mammoth_king', at: 20 },
    },
  ],
  loot: [
    { itemId: 'a_frostweave', weight: 2 },
    { itemId: 'r_mammoth_tusk', weight: 2 },
    { itemId: 'a_hide', weight: 1 },
    { itemId: 'a_chitin', weight: 1 },
    { itemId: 'r_amber', weight: 1 },
    { itemId: 'w_bone_spear', weight: 1 },
  ],
  audio: { music: 'music/era4_tundra', ambient: 'amb/tundra', sfxSet: 'beast' },
};
